import { acknowledgeOrder, deleteOrder } from '@/api/orders';

// TCA-26101818: renderer-lifetime custody, not a server lock or durable receipt.
// Claims contain no credentials or order details and survive provider retirement.
const claims = new Set();
const observers = new Set();
const overlaps = (a, b) => a.endpoint === b.endpoint && (!a.tenant || !b.tenant || a.tenant === b.tenant);
const broadcast = (claim, settled = false) => observers.forEach(observer => observer(claim, settled));

function refused(error, kind) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if ([401, 403].includes(status)) return 'authority';
  if (status === 409 && detail === 'This record changed before your update completed. Refresh and try again.') return 'refused';
  if (kind === 'approve' && ((status === 404 && detail === 'Order not found')
    || (status === 400 && typeof detail === 'string' && detail.startsWith('Cannot transition from ')))) return 'refused';
  if (kind === 'reject' && status === 409 && typeof detail === 'string'
    && /^Password confirmation required to delete (on route|delivered) order$/.test(detail)) return 'refused';
  return 'unknown';
}

function accepted(value, kind, id) {
  if (kind === 'approve') return value?.success === true && value.order_id === id && value.new_status === 'accepted'
    ? 'accepted' : 'unknown';
  if (value?.message === 'Order deleted successfully') return 'accepted';
  return value?.message === 'Order already deleted' ? 'absent' : 'unknown';
}

export function createIncomingDecisionOwner(capture) {
  const listeners = new Set();
  let active = false;
  let reviewClaims = null;
  let snapshot = { pending: false, uncertain: false, reviewReady: false, writable: false };
  const matching = owner => owner ? [...claims].filter(claim => overlaps(owner.resource, claim)) : [];
  const notify = () => {
    const owner = capture();
    const related = matching(owner);
    const pending = related.some(claim => claim.state === 'pending');
    const uncertain = related.some(claim => claim.state === 'unknown');
    snapshot = { pending, uncertain, reviewReady: !!reviewClaims && !pending && uncertain,
      writable: active && !!owner && !pending && !uncertain };
    listeners.forEach(listener => listener());
  };
  const changed = (claim, settled) => {
    const owner = capture();
    if (!owner || !overlaps(owner.resource, claim)) return;
    reviewClaims = null;
    owner.invalidate();
    notify();
    // A current owner observes server state independently; it never adopts an
    // old attempt's row removals, dialog settlement or success message.
    if (settled) void owner.reconcile();
  };
  return {
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    getSnapshot: () => snapshot,
    activate() { active = true; observers.add(changed); notify(); },
    retire() { active = false; reviewClaims = null; observers.delete(changed); },
    update() { reviewClaims = null; notify(); },
    createScreen(key) { return { key, active: false }; },
    async review() {
      const owner = capture();
      if (!active || !owner || matching(owner).some(claim => claim.state === 'pending')) return false;
      const before = matching(owner);
      reviewClaims = null; notify();
      const ok = await owner.review();
      if (!active || !owner.current() || !ok) return false;
      const after = matching(owner);
      if (after.length !== before.length || after.some(claim => !before.includes(claim))) return false;
      reviewClaims = { key: owner.key, claims: after }; notify();
      return true;
    },
    acknowledgeUncertainty() {
      const owner = capture();
      if (!active || !owner || reviewClaims?.key !== owner.key || !snapshot.reviewReady) return;
      const reviewed = reviewClaims.claims;
      reviewClaims = null;
      for (const claim of reviewed) if (claim.state === 'unknown') claims.delete(claim);
      // Invalidate other panels' review permissions without changing their data.
      for (const claim of reviewed) broadcast(claim);
      notify();
    },
    async run(kind, ids, screen) {
      const owner = capture();
      if (!active || !owner || !screen.active || screen.key !== owner.key || matching(owner).length
        || !['approve', 'reject'].includes(kind) || !ids.length
        || (kind === 'reject' && ids.length !== 1) || new Set(ids).size !== ids.length
        || ids.some(id => !owner.contains(id))) return { state: 'blocked' };
      ids = [...ids];
      const current = () => active && screen.active && screen.key === owner.key && owner.current();
      const claim = { ...owner.resource, state: 'pending' };
      claims.add(claim); broadcast(claim);
      const result = { state: 'complete', approved: 0, rejected: 0, absent: 0, refused: 0, unknown: 0, unattempted: ids.length };
      let uncertain = false;
      try {
        for (const id of ids) {
          if (!current()) break;
          if (!owner.contains(id)) { result.refused++; result.unattempted--; continue; }
          let outcome;
          result.unattempted--;
          try {
            const value = kind === 'approve' ? await acknowledgeOrder(id, owner.config)
              : await deleteOrder(id, undefined, owner.config);
            outcome = accepted(value, kind, id);
          } catch (error) { outcome = refused(error, kind); }
          // Classify effects even after retirement, without publishing details.
          if (outcome === 'unknown') { uncertain = true; result.unknown++; }
          if (!current()) break;
          if (outcome === 'accepted' || outcome === 'absent') {
            owner.remove(id);
            if (outcome === 'absent') result.absent++;
            else if (kind === 'approve') result.approved++;
            else result.rejected++;
          } else if (outcome !== 'unknown') result.refused++;
          if (outcome === 'unknown' || outcome === 'authority') break;
        }
      } finally {
        if (uncertain) claim.state = 'unknown';
        else claims.delete(claim);
        broadcast(claim, true);
      }
      return current() ? result : { state: 'retired' };
    },
  };
}
